// Helpers for sending date-range parameters to analytics / agent endpoints
// that bucket aggregates by the user's *local* calendar day.
//
// Wire format: RFC3339 with offset (e.g. "2026-05-19T00:00:00+08:00") so the
// server gets both the precise UTC instant and the browser's local-zone
// offset. Go's time.Parse(time.RFC3339, …) recovers a time.Time carrying
// both pieces; ClickHouse's toDate(ts, $tz) can then bucket events by the
// user's local day rather than the underlying UTC day.

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Format a Date as "YYYY-MM-DDTHH:mm:ss±HH:MM" using the browser's local
 * timezone offset. The Date is built from local components (e.g. local
 * midnight via setHours(0,0,0,0)); the offset segment is read from the
 * Date's runtime timezone so the result is semantically the local
 * instant the user picked, expressed in RFC3339.
 *
 *   kind = 'start' → snap to local-day start (00:00:00.000)
 *   kind = 'end'   → snap to local-day end   (23:59:59.999)
 *
 * UTC+8 picking May 19 → "2026-05-19T00:00:00+08:00" / "23:59:59+08:00".
 */
export function toIsoLocal(d: Date, kind: 'start' | 'end'): string {
  const local = new Date(d);
  if (kind === 'end') local.setHours(23, 59, 59, 999);
  else local.setHours(0, 0, 0, 0);
  return toIsoLocalInstant(local);
}

/**
 * Format an arbitrary Date as RFC3339-with-offset *without* snapping to
 * any local-day boundary. Used for rolling windows ("last 7 days", "now",
 * "24 hours ago") where the picker means "this exact instant", not a
 * calendar day.
 */
export function toIsoLocalInstant(d: Date): string {
  const offMin = -d.getTimezoneOffset(); // UTC+8 → +480
  const sign = offMin >= 0 ? '+' : '-';
  const hh = pad(Math.floor(Math.abs(offMin) / 60));
  const mm = pad(Math.abs(offMin) % 60);
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `${sign}${hh}:${mm}`
  );
}

/**
 * Format an ISO timestamp string (e.g. "2026-06-02T07:29:20Z") as a
 * human-readable local datetime: "2026/06/02 07:29:20".
 * Returns the raw string unchanged if it cannot be parsed.
 */
export function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return (
    `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}` +
    ` ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

/**
 * Return the browser's IANA timezone name (e.g. "Asia/Shanghai") when the
 * zone observes DST or has a non-hour offset (e.g. India UTC+5:30);
 * otherwise undefined so the caller can omit the `tz` query param.
 *
 * Pure RFC3339-with-offset is sufficient for fixed-offset zones — the
 * server constructs an Etc/GMT±N fallback from the embedded offset. DST
 * and half-hour zones can't be expressed via Etc/GMT, so they need the
 * full IANA name to drive ClickHouse's TZ-aware date functions.
 */
export function ianaTzIfNeeded(): string | undefined {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const y = new Date().getFullYear();
  const offJan = new Date(y, 0, 1).getTimezoneOffset();
  const offJul = new Date(y, 6, 1).getTimezoneOffset();
  if (offJan !== offJul || offJan % 60 !== 0) return tz;
  return undefined;
}
